import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useExperienceStore } from '@/lib/stores/experience-store';

export function CreatorSpotlight() {
  const navigate = useNavigate();
  const { spotlights, loadSpotlights } = useExperienceStore();
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const retryCount = useRef(0);
  const maxRetries = 3;

  useEffect(() => {
    const fetchSpotlightsData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await loadSpotlights();
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to load spotlights:', err);
        setError('Failed to load creator spotlights');
        
        // Retry logic with exponential backoff
        if (retryCount.current < maxRetries) {
          const delay = Math.pow(2, retryCount.current) * 1000;
          retryCount.current++;
          setTimeout(fetchSpotlightsData, delay);
        }
      }
    };

    fetchSpotlightsData();
  }, [loadSpotlights]);

  if (isLoading) {
    return (
      <section className="py-24 bg-earth-900/95 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="relative aspect-[3/4] animate-pulse bg-earth-800/50 rounded-lg" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error || !spotlights || spotlights.length === 0) {
    return null;
  }

  return (
    <section className="py-24 bg-earth-900/95 relative overflow-hidden">
      <div className="absolute inset-0 bg-texture opacity-5" />
      
      <div className="container mx-auto px-4 relative">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {spotlights.slice(0, 3).map((spotlight) => (
            <motion.div
              key={spotlight.id}
              className="relative aspect-[3/4] cursor-pointer group"
              onClick={() => navigate(`/creators/${spotlight.creator.id}`)}
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <img
                src={spotlight.media[0]?.url || spotlight.creator.profile_image}
                alt={spotlight.title}
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-earth-900 via-earth-900/50 to-transparent opacity-80" />

              {/* Content */}
              <div className="absolute inset-x-0 bottom-0 p-8">
                <div className="space-y-2">
                  <h3 className="text-2xl font-display text-sand-100">
                    {spotlight.creator.full_name}
                  </h3>
                  <p className="text-xl font-display text-sand-300">
                    {spotlight.title}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}